import { yupResolver } from "@hookform/resolvers/yup";
import Alert from "antd/lib/alert";
import Form from "antd/lib/form";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { format, isValid, parse } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import type { Path } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormField } from "@sol/forms";
import ImageUploadInput from "@sol/forms/inputs/ImageUploadInput";
import { IClassroomTrainingAction, useTrainingTemplateQuery } from "@sol/sdk";
import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";
import { DatePicker } from "@sol/uikit/data-entry/DatePicker/DatePicker";
import { Input } from "@sol/uikit/data-entry/Input/Input";
import { Modal } from "@sol/uikit/feedback/Modal/Modal";
import { Button } from "@sol/uikit/general/Button/Button";

import { FactorySelect } from "@/entities/factory";
import { LanguageSelect } from "@/entities/language";
import { LocationSelect } from "@/entities/location";
import { getApiErrorMessage, getApiFieldViolations } from "@/shared/lib/api-errors";

import { ClassroomTrainingActionEditItem } from "../components/ClassroomTrainingActionEditItem";
import {
    buildClassroomTemplateSchema,
    type ClassroomTemplateFormValues,
    staticSchema,
} from "../lib/classroomTemplateSchema";
import { mapViolationPathToFormPath } from "../lib/mapViolationPathToFormPath";
import { hasTrainingActionStarted, requiresPeriodsReimportConfirmation } from "../lib/periodsReimport";

export type { ClassroomTemplateFormValues } from "../lib/classroomTemplateSchema";

type CustomTimePickerProps = Omit<React.ComponentProps<typeof DatePicker.TimePicker>, "value" | "onChange"> & {
    value?: string | Date | null;
    onChange?: (value: string | null) => void;
};

const CustomTimePicker = ({ value, onChange, ...props }: CustomTimePickerProps) => {
    const parsedValue = value instanceof Date ? value : value ? parse(value, "HH:mm", new Date()) : null;

    return (
        <DatePicker.TimePicker
            format="HH:mm"
            showNow={false}
            needConfirm={false}
            hideDisabledOptions
            value={parsedValue && isValid(parsedValue) ? parsedValue : null}
            onChange={time => onChange?.(time ? format(time as Date, "HH:mm") : null)}
            {...props}
        />
    );
};

type Props = {
    className?: string;
    trainingTemplateIri?: string;
    classroomTrainingActions?: IClassroomTrainingAction[];
    onSubmit: (data: ClassroomTemplateFormValues) => void;
    defaultValues?: Partial<ClassroomTemplateFormValues>;
    onImageDelete?: () => Promise<void>;
    hasPlannedScenarioContent?: boolean;
};

export function ClassroomTemplateForm({
    className,
    trainingTemplateIri,
    classroomTrainingActions,
    onSubmit,
    defaultValues,
    onImageDelete,
    // an unknown scenario is warned about rather than silently reimported
    hasPlannedScenarioContent = true,
}: Props) {
    const [t] = useTranslation();

    const schema = useMemo(() => buildClassroomTemplateSchema(t), [t]);

    const methods = useForm<ClassroomTemplateFormValues>({
        mode: "all",
        resolver: yupResolver(schema),
        defaultValues: defaultValues ? staticSchema.cast(defaultValues) : schema.getDefault(),
    });

    const {
        handleSubmit,
        trigger,
        setError,
        getValues,
        formState: { isSubmitting, isValid },
    } = methods;

    const [serverError, setServerError] = useState<string | null>(null);
    const [modal, modalContextHolder] = Modal.useModal();

    const formRef = useRef<HTMLFormElement>(null);

    const today = useMemo(() => new Date(), []);

    const confirmPeriodsReimport = () =>
        modal.confirm({
            title: t("form.classroomTemplateEdit.trainingActions.erpFile.confirm.title"),
            content: t("form.classroomTemplateEdit.trainingActions.erpFile.confirm.description"),
            okText: t("form.classroomTemplateEdit.trainingActions.erpFile.confirm.submit"),
            cancelText: t("form.classroomTemplateEdit.trainingActions.erpFile.confirm.cancel"),
            centered: true,
            okButtonProps: { "data-cy": "classroom-edit-periods-reimport-confirm" },
            cancelButtonProps: { "data-cy": "classroom-edit-periods-reimport-cancel" },
        });

    const scrollToFirstError = () => {
        // wait for the error messages to be committed to the DOM before scrolling
        requestAnimationFrame(() => {
            // covers Form.Item errors, inputs flagged with status="error" (e.g. daily hours pickers)
            // and the global error alert
            const target = formRef.current?.querySelector(
                '.ant-form-item-has-error, [class*="-status-error"], .ant-alert-error',
            );
            target?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    };

    const sortedClassroomActions = useMemo(
        () => [...(classroomTrainingActions ?? [])].sort((a, b) => a.rank - b.rank),
        [classroomTrainingActions],
    );

    useEffect(() => {
        trigger();
    }, [trigger]);

    const handleFormSubmit = async (data: ClassroomTemplateFormValues) => {
        if (
            requiresPeriodsReimportConfirmation(data.trainingActions, hasPlannedScenarioContent) &&
            !(await confirmPeriodsReimport())
        ) {
            return;
        }

        setServerError(null);
        try {
            await onSubmit(data);
        } catch (error) {
            const violations = getApiFieldViolations(error);
            if (violations.length > 0) {
                const unmappedMessages: string[] = [];
                violations.forEach(({ fieldPath, message }) => {
                    const formPath = mapViolationPathToFormPath(fieldPath);
                    // setError silently ignores paths that don't match a registered field:
                    // surface those violations globally instead of losing them
                    if (
                        /^trainingActions\.\d+\.(rank|erpIdentifier|erpFile)$/.test(formPath) ||
                        getValues(formPath as Path<ClassroomTemplateFormValues>) !== undefined
                    ) {
                        setError(formPath as Path<ClassroomTemplateFormValues>, { type: "server", message });
                    } else {
                        unmappedMessages.push(message);
                    }
                });
                if (unmappedMessages.length > 0) {
                    setServerError(unmappedMessages.join(" "));
                }
            } else {
                setServerError(getApiErrorMessage(error) ?? t("form.classroomTemplateEdit.error.generic"));
            }
            scrollToFirstError();
        }
    };

    const { data: trainingTemplate } = useTrainingTemplateQuery(
        { trainingTemplate: trainingTemplateIri },
        { enabled: !!trainingTemplateIri },
    );

    const size = "middle" as const;

    return (
        <FormProvider {...methods}>
            <form ref={formRef} className={clsx("flex flex-col", className)} onSubmit={handleSubmit(handleFormSubmit)}>
                <div className="flex flex-col items-start gap-ant-sm sm:flex-row sm:items-end sm:justify-between">
                    <FormField
                        className="self-start"
                        label={t("form.addNewClassroom.language.label")}
                        name="language"
                        required
                        render={({ field }) => <LanguageSelect size={size} {...field} />}
                    />
                    {trainingTemplate && (
                        <div className="bg-ant-color-fill-secondary mb-ant rounded-ant-lg border-ant border-ant-secondary bg-ant-fill-quaternary px-ant py-ant-sm text-right">
                            <Typography.Text type="secondary" className="block text-xs tracking-wide uppercase">
                                {t("form.classroomTemplateEdit.trainingTemplate.label")}
                            </Typography.Text>
                            <Typography.Text strong>{trainingTemplate.name}</Typography.Text>
                        </div>
                    )}
                </div>

                <FormField
                    label={t("form.addNewClassroom.title.label")}
                    name="title"
                    required
                    render={({ field }) => (
                        <Input
                            size={size}
                            maxLength={100}
                            showCount
                            placeholder={t("form.addNewClassroom.title.placeholder")}
                            {...field}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.description.label")}
                    name="description"
                    render={({ field }) => (
                        <Input.TextArea
                            size={size}
                            maxLength={300}
                            showCount
                            placeholder={t("form.addNewClassroom.description.placeholder")}
                            {...field}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.location.label")}
                    name="location"
                    required
                    render={({ field }) => <LocationSelect size={size} {...field} />}
                />

                <FormField
                    label={t("form.addNewClassroom.factory.label")}
                    name="factory"
                    required
                    render={({ field }) => (
                        <FactorySelect
                            size={size}
                            placeholder={t("form.addNewClassroom.factory.placeholder")}
                            {...field}
                        />
                    )}
                />

                <Form.Item label={t("form.classroomTemplateEdit.dailyHours.label")} layout="vertical">
                    <div className="flex gap-ant-md">
                        <Controller
                            name="dailyStartHour"
                            render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
                                <div className="flex flex-1 flex-col">
                                    <CustomTimePicker
                                        size={size}
                                        placeholder={t(
                                            "features.classroomCreation.ClassroomCreateGeneralInformationFieldset.dailyStartHour.placeholder",
                                        )}
                                        value={value}
                                        onChange={onChange}
                                        status={error ? "error" : undefined}
                                        {...field}
                                    />
                                    {error?.message && (
                                        <Typography.Text type="danger" className="mt-ant-xs text-xs">
                                            {error.message}
                                        </Typography.Text>
                                    )}
                                </div>
                            )}
                        />
                        <Controller
                            name="dailyEndHour"
                            render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
                                <div className="flex flex-1 flex-col">
                                    <CustomTimePicker
                                        size={size}
                                        placeholder={t(
                                            "features.classroomCreation.ClassroomCreateGeneralInformationFieldset.dailyEndHour.placeholder",
                                        )}
                                        value={value}
                                        onChange={onChange}
                                        status={error ? "error" : undefined}
                                        {...field}
                                    />
                                    {error?.message && (
                                        <Typography.Text type="danger" className="mt-ant-xs text-xs">
                                            {error.message}
                                        </Typography.Text>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                </Form.Item>

                <FormField
                    label={t("form.addNewClassroom.image.label")}
                    name="image"
                    render={({ field }) => (
                        <ImageUploadInput className="aspect-video" {...field} onDelete={onImageDelete} />
                    )}
                />

                {trainingTemplate &&
                    trainingTemplate.trainingActions &&
                    trainingTemplate.trainingActions.length > 0 && (
                        <div className="mt-ant-sm mb-ant-lg">
                            <Typography.Text type="secondary" className="text-xs tracking-wide uppercase">
                                {t("form.classroomTemplateEdit.trainingActions.label")}
                            </Typography.Text>
                            <div className="mt-ant-xs flex flex-col gap-ant">
                                {[...trainingTemplate.trainingActions]
                                    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity))
                                    .map((action, idx) => {
                                        const classroomAction = sortedClassroomActions[idx];
                                        const actionTitle = t(
                                            `features.classroomSyllabus.SyllabusTrainingAction.actionType.${action.type}`,
                                        );

                                        return (
                                            <ClassroomTrainingActionEditItem
                                                key={action["@id"] ?? idx}
                                                rank={classroomAction?.rank ?? idx}
                                                actionTitle={actionTitle}
                                                description={action.description}
                                                pedagogicalHours={action.pedagogicalHours}
                                                formIndex={idx}
                                                started={hasTrainingActionStarted(classroomAction?.startDate, today)}
                                            />
                                        );
                                    })}
                            </div>
                        </div>
                    )}

                {serverError && <Alert type="error" message={serverError} className="mb-ant" />}

                <div className="mt-ant-lg flex flex-row items-center justify-end gap-ant">
                    <Button
                        disabled={isSubmitting || !isValid}
                        loading={isSubmitting}
                        htmlType="submit"
                        type="primary"
                        icon={<ArrowRight variant="antd" />}
                        iconPosition="end"
                    >
                        {t("form.classroomTemplateEdit.submit.label")}
                    </Button>
                </div>
            </form>
            {modalContextHolder}
        </FormProvider>
    );
}
